"use client";

import { useRef, useCallback } from "react";

interface SliderProps {
  min: number;
  max: number;
  value: number;
  onChange: (value: number) => void;
  label?: string;
  displayValue?: string;
  className?: string;
}

export function Slider({
  min,
  max,
  value,
  onChange,
  label,
  displayValue,
  className = "",
}: SliderProps) {
  const percent = ((value - min) / (max - min)) * 100;
  const trackRef = useRef<HTMLDivElement>(null);

  const updateValue = useCallback(
    (clientX: number) => {
      const track = trackRef.current;
      if (!track) return;
      const rect = track.getBoundingClientRect();
      const ratio = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
      onChange(Math.round(min + ratio * (max - min)));
    },
    [min, max, onChange],
  );

  const onPointerDown = useCallback(
    (e: React.PointerEvent) => {
      e.preventDefault();
      (e.target as HTMLElement).setPointerCapture(e.pointerId);
      updateValue(e.clientX);
    },
    [updateValue],
  );

  const onPointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (e.buttons === 0) return;
      updateValue(e.clientX);
    },
    [updateValue],
  );

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      {label && (
        <span className="text-xs text-text-secondary whitespace-nowrap">
          {label}
        </span>
      )}
      <div
        ref={trackRef}
        className="relative flex-1 min-w-[80px] h-4 flex items-center cursor-pointer"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
      >
        {/* Track */}
        <div className="absolute left-0 right-0 h-[3px] rounded-full bg-bg-elevated">
          <div
            className="absolute left-0 top-0 bottom-0 rounded-full"
            style={{
              width: `${percent}%`,
              backgroundColor: "var(--color-accent)",
            }}
          />
        </div>
        {/* Thumb */}
        <div
          className="absolute w-2.5 h-2.5 rounded-full -translate-x-1/2 transition-transform duration-150 hover:scale-110"
          style={{
            left: `${percent}%`,
            backgroundColor: "var(--color-accent-bright)",
            boxShadow: "0 0 6px rgba(139,92,246,0.3)",
          }}
        />
      </div>
      {displayValue && (
        <span className="text-xs text-text-primary font-medium tabular-nums min-w-[2.5rem] text-right">
          {displayValue}
        </span>
      )}
    </div>
  );
}
